import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/profile-components/Navbar";
import Topbar from "../components/profile-components/Topbar";
import useCurrentUserAccess from "../hooks/useCurrentUserAccess";
import "./Templates.css";
import "./SalesPages.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://agent-book-crm-backend.onrender.com";

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState(null);
  const [restoringDefaults, setRestoringDefaults] = useState(false);

  const navigate = useNavigate();
  const {
    loading: accessLoading,
    platformRole,
    accountRole,
  } = useCurrentUserAccess();

  const isPlatformAdmin = platformRole?.key_name === "platform_admin";
  const isPlatformEmployee = platformRole?.key_name === "platform_employee";
  const isAccountAdmin = accountRole?.key_name === "account_admin";
  const isAccountUser = accountRole?.key_name === "account_user";

  const isPlatformSide = isPlatformAdmin || isPlatformEmployee;
  const isSubscriberSide = isAccountAdmin || isAccountUser;

  useEffect(() => {
    loadTemplates();
  }, []);

  const sortedTemplates = useMemo(() => {
    const rows = Array.isArray(templates) ? [...templates] : [];

    if (isPlatformSide) {
      rows.sort((a, b) => {
        if (
          (b.is_default_template ? 1 : 0) !==
          (a.is_default_template ? 1 : 0)
        ) {
          return (
            (b.is_default_template ? 1 : 0) -
            (a.is_default_template ? 1 : 0)
          );
        }

        return String(a.name || "").localeCompare(String(b.name || ""));
      });

      return rows;
    }

    rows.sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""))
    );

    return rows;
  }, [templates, isPlatformSide]);

  const loadTemplates = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/templates/email`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Failed to load templates");

      const data = await res.json();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading templates:", err.message);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (id) => {
    try {
      setWorkingId(`copy-${id}`);

      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_BASE_URL}/api/templates/email/${id}/duplicate`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!res.ok) throw new Error("Duplicate failed");

      await loadTemplates();
    } catch (err) {
      alert("Error duplicating template: " + err.message);
    } finally {
      setWorkingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this template?")) {
      return;
    }

    try {
      setWorkingId(`delete-${id}`);

      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/templates/email/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Delete failed");

      await loadTemplates();
    } catch (err) {
      alert("Error deleting template: " + err.message);
    } finally {
      setWorkingId(null);
    }
  };

  const handleSample = (id) => {
    const email = window.prompt("Enter email address to send sample to:");
    if (!email) return;
    alert(`Sample requested for ${email}. (Not yet implemented)`);
  };

  const handleDefaultToggle = async (template) => {
    try {
      setWorkingId(`default-${template.id}`);

      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_BASE_URL}/api/templates/email/${template.id}/default-status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            is_default_template: !template.is_default_template,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.error || "Failed to update default template status"
        );
      }

      await loadTemplates();
    } catch (err) {
      alert("Error updating default template status: " + err.message);
    } finally {
      setWorkingId(null);
    }
  };

  const handleRestoreDefaults = async () => {
    try {
      setRestoringDefaults(true);

      const token = localStorage.getItem("token");
      const res = await fetch(
        `${API_BASE_URL}/api/templates/email/restore-defaults`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to restore default templates");
      }

      await loadTemplates();

      alert(
        data.restored_count > 0
          ? `${data.restored_count} default template(s) restored successfully.`
          : "No missing default templates were found."
      );
    } catch (err) {
      alert("Error restoring default templates: " + err.message);
    } finally {
      setRestoringDefaults(false);
    }
  };

  const defaultCount = useMemo(
    () => sortedTemplates.filter((t) => t.is_default_template).length,
    [sortedTemplates]
  );

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">Email Templates</h1>
            <p className="sales-subtitle">
              Manage reusable email templates, default templates, and custom copies.
            </p>

            <div className="sales-hero-meta">
              <span className="sales-chip sales-chip-neutral">
                {sortedTemplates.length} Template
                {sortedTemplates.length === 1 ? "" : "s"}
              </span>

              {isPlatformSide && (
                <span className="sales-chip sales-chip-outline">
                  {defaultCount} Default
                </span>
              )}

              {isSubscriberSide && (
                <span className="sales-chip sales-chip-outline">
                  Subscriber Library
                </span>
              )}
            </div>
          </div>

          <div className="sales-header-actions">
            <button
              className="sales-primary-btn"
              onClick={() => navigate("/templates/new")}
            >
              + Add Email Template
            </button>

            {!accessLoading && isSubscriberSide && (
              <button
                className="sales-secondary-btn"
                onClick={handleRestoreDefaults}
                disabled={restoringDefaults}
              >
                {restoringDefaults
                  ? "Restoring..."
                  : "Restore Default Templates"}
              </button>
            )}
          </div>
        </div>

        <div className="sales-table-card">
          <div className="sales-table-header">
            <div>
              <h2>Template Library</h2>
              <p>Review template details, subjects, and available actions.</p>
            </div>
          </div>

          <div className="sales-table-wrap">
            <table className="sales-table templates-table-modern">
              <thead>
                <tr>
                  <th>Name</th>
                  {isSubscriberSide && <th>Template Type</th>}
                  <th>Subject</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={isSubscriberSide ? 4 : 3}
                      className="templates-empty-cell"
                    >
                      Loading templates...
                    </td>
                  </tr>
                ) : sortedTemplates.length === 0 ? (
                  <tr>
                    <td
                      colSpan={isSubscriberSide ? 4 : 3}
                      className="templates-empty-cell"
                    >
                      No templates found.
                    </td>
                  </tr>
                ) : (
                  sortedTemplates.map((t) => (
                    <tr key={t.id}>
                      <td className="template-name-cell">
                        <div className="template-name-block">
                          <button
                            type="button"
                            className="template-name-link"
                            onClick={() => navigate(`/templates/${t.id}`)}
                          >
                            {t.name}
                          </button>

                          {isPlatformSide && t.is_default_template && (
                            <div>
                              <span className="sales-chip sales-chip-neutral">
                                Default
                              </span>
                            </div>
                          )}
                        </div>
                      </td>

                      {isSubscriberSide && (
                        <td>
                          {t.source_template_id ? (
                            <span className="sales-chip sales-chip-outline">
                              Default Copy
                            </span>
                          ) : (
                            <span className="sales-chip sales-chip-neutral">
                              Custom
                            </span>
                          )}
                        </td>
                      )}

                      <td className="template-subject-cell">
                        <div className="template-subject-text">
                          {t.subject || "—"}
                        </div>
                      </td>

                      <td>
                        <div className="template-actions-modern">
                          <button
                            className="sales-secondary-btn template-table-btn"
                            onClick={() => navigate(`/templates/${t.id}`)}
                          >
                            Edit
                          </button>

                          <button
                            className="sales-secondary-btn template-table-btn"
                            onClick={() => handleCopy(t.id)}
                            disabled={workingId === `copy-${t.id}`}
                          >
                            {workingId === `copy-${t.id}` ? "Copying..." : "Copy"}
                          </button>

                          {isPlatformSide && (
                            <button
                              className="sales-secondary-btn template-table-btn"
                              onClick={() => handleDefaultToggle(t)}
                              disabled={workingId === `default-${t.id}`}
                            >
                              {workingId === `default-${t.id}`
                                ? "Saving..."
                                : t.is_default_template
                                  ? "Remove Default"
                                  : "Set Default"}
                            </button>
                          )}

                          <button
                            className="sales-secondary-btn template-table-btn"
                            onClick={() => handleSample(t.id)}
                          >
                            Sample
                          </button>

                          <button
                            className="sales-danger-btn template-table-btn"
                            onClick={() => handleDelete(t.id)}
                            disabled={workingId === `delete-${t.id}`}
                          >
                            {workingId === `delete-${t.id}`
                              ? "Deleting..."
                              : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}